// src/pages/specialties/DermatologyLanding.jsx
import React, { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ShieldCheck, Star, Clock, MapPin, Search, ArrowRight, Stethoscope, X, Users } from "lucide-react";

import SiteFooter from "../../LPFooter";
import DoctorListSEO from "../../DoctorListSeo";

import { collection, getDocs, query, where, limit, orderBy } from "firebase/firestore";
import { db } from "../../firebase";

import "./DermatologyLanding.css";

const normLang = (x) => ((x || "").toLowerCase() === "en" ? "en" : "sq");

const normalizeSq = (s) =>
  (s || "")
    .toString()
    .trim()
    .toLowerCase()
    .replace(/ë/g, "e")
    .replace(/ç/g, "c");

const makeDoctorAvatar = (name) => {
  const initials = (name || "D")
    .split(" ")
    .map((n) => n?.[0] || "")
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const colors = [
    { bg: "#3b82f6", text: "#FFF" },
    { bg: "#6366f1", text: "#FFF" },
    { bg: "#8b5cf6", text: "#FFF" },
    { bg: "#10b981", text: "#FFF" },
  ];
  const c = colors[(name || "").length % colors.length];

  return `data:image/svg+xml,${encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 200 200">
      <rect width="200" height="200" fill="${c.bg}"/>
      <text x="50%" y="50%" text-anchor="middle" dy=".35em" fill="${c.text}"
        font-size="80" font-family="Arial" font-weight="bold">${initials}</text>
    </svg>
  `)}`;
};

const matchesDermatology = (doc) => {
  const hay = [
    doc.specialtyKey,
    doc.specialty,
    doc.specialtySq,
    doc.specialtyEn,
    doc.specKey,
    doc.spec,
  ]
    .filter(Boolean)
    .map(normalizeSq)
    .join(" ");

  return hay.includes("dermatolog") || hay.includes("dermatology");
};

const locationLabel = (v) => {
  if (!v) return "—";
  if (typeof v === "string") return v;
  if (typeof v === "object") return v.address || v.formatted_address || v.name || "—";
  return String(v);
};

const copy = {
  sq: {
    title: "Dermatologji në Tiranë & Shqipëri",
    subtitle:
      "Gjej dermatologë të verifikuar për akne, ekzema, alergji të lëkurës, rënie flokësh, nishane dhe psoriazë. Shiko vlerësime dhe rezervo online shpejt.",
    searchPlaceholder: "Kërko mjek, klinikë, qytet ose problem (akne, ekzema...)",
    ctaPrimary: "Shiko dermatologët",
    ctaSticky: "Shiko listën",
    trust: [
      { icon: "verified", title: "Mjekë të verifikuar", sub: "Profile të kontrolluara" },
      { icon: "reviews", title: "Vlerësime reale", sub: "Komentet e pacientëve" },
      { icon: "time", title: "Orar i lirë", sub: "Rezervim i shpejtë" },
      { icon: "locations", title: "Afër teje", sub: "Tiranë • Durrës • Elbasan" },
    ],
    popular: {
      title: "Arsyet më të shpeshta",
      items: ["Akne", "Ekzema", "Alergji të lëkurës", "Rënie flokësh", "Nishane", "Psoriazë"],
    },
    faq: {
      title: "Pyetje të shpeshta",
      items: [
        {
          q: "Kur duhet të shkoj te dermatologu?",
          a: "Kur ke skuqje të vazhdueshme, kruarje, akne që përkeqësohen, nishane që ndryshojnë, ose rënie flokësh të pazakontë.",
        },
        { q: "A mund të rezervoj online?", a: "Po. Zgjidh dermatologun, shiko oraret dhe rezervo në pak sekonda." },
        { q: "A ka konsultë online?", a: "Disa mjekë e ofrojnë — kjo shfaqet te profili i tyre." },
        { q: "Sa kushton një vizitë te dermatologu?", a: "Tarifat ndryshojnë sipas mjekut/klinikës. Te lista mund të shohësh çmimet ose orientimin e tarifës." },
      ],
    },
    otherLinksTitle: "Shiko edhe",
    heroPill: "Lëkurë • Flokë • Thonj",
    specialtyTag: "Specialitet",
    topTitle: "Dermatologët kryesorë",
    backHome: "Kthehu në fillim",
    empty: "Nuk u gjetën dermatologë ende.",
    emptyHint: "Provo të heqësh filtrin ose shiko listën e përgjithshme.",
    viewProfile: "Shiko profilin",
    seePrices: "Tarifa",
    clear: "Pastro",
    results: "Rezultate",
    hint: "Këshillë: kërko sipas klinikës, qytetit ose problemit.",
    canonicalPath: "/sq/specialties/dermatologji",
    ogImage: "/og/dermatologji.jpg",
    verified: "Verifikuar",
    followers: "Ndjekës",
    years: "vite",
  },
  en: {
    title: "Dermatology in Albania | MikDok",
    subtitle:
      "Find verified dermatologists for acne, eczema, skin allergies, hair loss, moles and psoriasis. Read reviews and book fast online.",
    searchPlaceholder: "Search doctor, clinic, city or issue (acne, eczema...)",
    ctaPrimary: "Browse dermatologists",
    ctaSticky: "View list",
    trust: [
      { icon: "verified", title: "Verified doctors", sub: "Profiles reviewed" },
      { icon: "reviews", title: "Real reviews", sub: "Patient feedback" },
      { icon: "time", title: "Available slots", sub: "Fast booking" },
      { icon: "locations", title: "Near you", sub: "Tirana • Durrës • Elbasan" },
    ],
    popular: {
      title: "Popular reasons",
      items: ["Acne", "Eczema", "Skin allergies", "Hair loss", "Moles", "Psoriasis"],
    },
    faq: {
      title: "FAQ",
      items: [
        {
          q: "When should I see a dermatologist?",
          a: "If you have persistent rash/itching, worsening acne, changing moles, or unusual hair loss.",
        },
        { q: "Can I book online?", a: "Yes. Pick a dermatologist, check availability, and book in seconds." },
        { q: "Is online consultation available?", a: "Some doctors offer it — you’ll see it on their profile." },
        { q: "How much does a dermatology visit cost?", a: "Fees vary by doctor/clinic. You can see an indicative fee on the list." },
      ],
    },
    otherLinksTitle: "Explore more",
    heroPill: "Skin • Hair • Nails",
    specialtyTag: "Specialty",
    topTitle: "Top dermatologists",
    backHome: "Back to home",
    empty: "No dermatologists found yet.",
    emptyHint: "Try clearing the filter or browse the general list.",
    viewProfile: "View profile",
    seePrices: "Fee",
    clear: "Clear",
    results: "Results",
    hint: "Tip: search by clinic name, city, or issue.",
    canonicalPath: "/en/specialties/dermatology",
    ogImage: "/og/dermatology.jpg",
    verified: "Verified",
    followers: "Followers",
    years: "yrs",
  },
};

function buildBreadcrumbJsonLd({ origin, lang, title }) {
  const base = `${origin}/${lang === "en" ? "en" : "sq"}`;
  const page = `${origin}${copy[lang].canonicalPath}`;
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "MikDok", item: base },
      { "@type": "ListItem", position: 2, name: title, item: page },
    ],
  };
}

function buildItemListJsonLd({ origin, lang, doctors, pageUrl }) {
  const items = (doctors || []).slice(0, 50).map((d, idx) => {
    const name = d.displayName || d.fullName || d.name || "Doctor";
    const url = `${origin}/doctor/${d.id}?lang=${lang}`;
    const specialty =
      d.specialtyLabel || d.specialtySq || d.specialtyEn || d.specialtyKey || d.specialty || "Physician";
    const loc = locationLabel(d.city || d.location || d.address);

    const rating =
      typeof d.rating === "number" && Number.isFinite(d.rating)
        ? {
            "@type": "AggregateRating",
            ratingValue: d.rating,
            bestRating: 5,
            worstRating: 1,
            ratingCount:
              typeof d.ratingCount === "number" && Number.isFinite(d.ratingCount) ? d.ratingCount : 1,
          }
        : undefined;

    return {
      "@type": "ListItem",
      position: idx + 1,
      url,
      item: {
        "@type": "Physician",
        name,
        url,
        medicalSpecialty: specialty,
        image: d.photoURL || d.photoUrl || undefined,
        aggregateRating: rating,
        address: loc !== "—" ? { "@type": "PostalAddress", addressLocality: loc } : undefined,
      },
    };
  });

  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: copy[lang].title,
    numberOfItems: items.length,
    url: pageUrl,
    itemListElement: items,
  };
}

function buildWebPageJsonLd({ pageUrl, title, description }) {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: title,
    description,
    url: pageUrl,
  };
}

function buildFaqJsonLd({ lang }) {
  const faqs = copy[lang]?.faq?.items || [];
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: f.a,
      },
    })),
  };
}

function TrustIcon({ type }) {
  const common = { size: 16 };
  if (type === "verified") return <ShieldCheck {...common} />;
  if (type === "reviews") return <Star {...common} />;
  if (type === "time") return <Clock {...common} />;
  return <MapPin {...common} />;
}

function SkeletonRow() {
  return (
    <div className="mdk-row mdk-row--skeleton" aria-hidden="true">
      <div className="mdk-photo skAvatar" />
      <div className="mdk-row-body">
        <div className="skLine skLine--lg" />
        <div className="skLine skLine--md" />
        <div className="skRow skRow--bottom">
          <div className="skLine skLine--sm" />
          <div className="skLine skLine--sm" />
        </div>
      </div>
      <div className="mdk-row-actions">
        <div className="skBtn" />
        <div className="skBtn skBtn--ghost" />
      </div>
    </div>
  );
}

function DoctorCardRow({ d, t, lang, onView, onBook }) {
  const name = d.displayName || d.fullName || d.name || "Doctor";
  const verified = d.verified === true;
  const ratingNum = typeof d.rating === "number" && Number.isFinite(d.rating) ? d.rating : null;

  const specialty =
    d.specialtyLabel || d.specialtySq || d.specialtyEn || d.specialtyKey || d.specialty || "General";

  const loc = locationLabel(d.city || d.location || d.address);
  const followerCount = typeof d.followerCount === "number" && Number.isFinite(d.followerCount) ? d.followerCount : 0;
  const experience = d.experienceYears ?? d.experience ?? null;
  const fee = d.feeText || (d.priceFrom ? `${d.priceFrom}€+` : "—");

  const fallback = useMemo(() => makeDoctorAvatar(name), [name]);
  const [imgSrc, setImgSrc] = useState(d.photoURL || d.photoUrl || fallback);

  const goView = (e) => {
    e.preventDefault();
    onView?.(d);
  };

  const goBook = (e) => {
    e.preventDefault();
    onBook?.(d);
  };

  return (
    <Link to={`/doctor/${d.id}?lang=${lang}`} className="mdk-row" aria-label={name}>
      <div className="mdk-photo">
        <img src={imgSrc} alt={name} loading="lazy" onError={() => setImgSrc(fallback)} />
      </div>

      <div className="mdk-row-main">
        <h3 className="mdk-name">
          {name}
          {verified && (
            <span className="mdk-verified" title={t.verified}>
              <ShieldCheck size={14} />
              {t.verified}
            </span>
          )}
        </h3>

        <div className="mdk-meta">
          <span className="mdk-metaItem">
            <Star size={14} />
            <b>{ratingNum ? ratingNum.toFixed(1) : "4.5"}</b>
          </span>

          <span className="mdk-metaItem">
            <Stethoscope size={14} />
            <b>{specialty}</b>
          </span>

          <span className="mdk-metaItem">
            <MapPin size={14} />
            <span>{loc}</span>
          </span>

          <span className="mdk-metaItem">
            <Users size={14} />
            <b>{followerCount}</b> {t.followers}
          </span>

          {experience != null && (
            <span className="mdk-metaItem">
              <Clock size={14} />
              <b>{experience}</b> {t.years}
            </span>
          )}
        </div>

        <div className="mdk-tags">
          <span className="mdk-tag">
            {t.seePrices}: <b style={{ marginLeft: 6 }}>{fee}</b>
          </span>
        </div>
      </div>

      <div className="mdk-row-actions" onClick={(e) => e.preventDefault()}>
        <button className="mdk-cta primary" onClick={goBook} type="button">
          {t.ctaPrimary}
        </button>
        <button className="mdk-cta secondary" onClick={goView} type="button">
          {t.viewProfile}
        </button>
      </div>
    </Link>
  );
}

export default function DermatologyLanding() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const lang = normLang(sp.get("lang"));
  const t = copy[lang];

  const listRef = useRef(null);

  const [qText, setQText] = useState("");
  const qDeferred = useDeferredValue(qText);

  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);

  const heroImage = "/specialties/1.jpg";

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setLoading(true);
        const refCol = collection(db, "doctors");

        let rows = [];
        try {
          const q1 = query(refCol, where("specialtyKey", "==", "dermatologji"), limit(120));
          const s1 = await getDocs(q1);
          rows = s1.docs.map((d) => ({ id: d.id, ...d.data() }));
        } catch (e1) {
          console.warn("DermatologyLanding q1 failed:", e1?.message || e1);
        }

        if (!rows.length) {
          let all = [];
          try {
            const q2 = query(refCol, orderBy("rankScore", "desc"), limit(300));
            const s2 = await getDocs(q2);
            all = s2.docs.map((d) => ({ id: d.id, ...d.data() }));
          } catch (e2) {
            console.warn("DermatologyLanding q2 failed:", e2?.message || e2);
            const q2b = query(refCol, limit(300));
            const s2b = await getDocs(q2b);
            all = s2b.docs.map((d) => ({ id: d.id, ...d.data() }));
          }

          rows = all.filter(matchesDermatology).slice(0, 120);
        }

        if (alive) setDoctors(rows);
      } catch (e) {
        console.error("DermatologyLanding fetch failed:", e);
        if (alive) setDoctors([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  const filtered = useMemo(() => {
    const s = (qDeferred || "").trim().toLowerCase();
    if (!s) return doctors;

    return doctors.filter((x) => {
      const hay = [
        x.displayName,
        x.fullName,
        x.name,
        x.clinicName,
        x.clinic,
        x.city,
        x.specialty,
        x.specialtySq,
        x.specialtyEn,
        x.specialtyKey,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return hay.includes(s);
    });
  }, [doctors, qDeferred]);

  const onBrowse = useCallback(() => {
    const pathLang = lang === "en" ? "en" : "sq";
    const city = "Tirana";
    const spec = lang === "en" ? "Dermatology" : "Dermatologji";
    nav(`/${pathLang}/${city}/${encodeURIComponent(spec)}?lang=${lang}`);
  }, [lang, nav]);

  const onScrollToList = useCallback(() => {
    listRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const onClear = useCallback(() => setQText(""), []);

  // SEO
  const origin = typeof window !== "undefined" ? window.location.origin : "https://mikdok.com";
  const pageUrl = origin + t.canonicalPath;

  const breadcrumbJsonLd = useMemo(
    () => buildBreadcrumbJsonLd({ origin, lang, title: t.title }),
    [origin, lang, t.title]
  );

  const itemListJsonLd = useMemo(
    () => buildItemListJsonLd({ origin, lang, doctors: filtered, pageUrl }),
    [origin, lang, filtered, pageUrl]
  );

  const webPageJsonLd = useMemo(
    () => buildWebPageJsonLd({ pageUrl, title: t.title, description: t.subtitle }),
    [pageUrl, t.title, t.subtitle]
  );

  const faqJsonLd = useMemo(() => buildFaqJsonLd({ lang }), [lang]);

  const hreflang = useMemo(() => {
    const sqUrl = `${origin}${copy.sq.canonicalPath}`;
    const enUrl = `${origin}${copy.en.canonicalPath}`;
    return [
      { hrefLang: "sq", href: sqUrl },
      { hrefLang: "en", href: enUrl },
      { hrefLang: "x-default", href: sqUrl },
    ];
  }, [origin]);

  return (
    <div className="specPage">
      <DoctorListSEO
        title={t.title}
        description={t.subtitle}
        canonical={pageUrl}
        ogImage={t.ogImage}
        lang={lang}
        hreflang={hreflang}
        jsonLd={[webPageJsonLd, breadcrumbJsonLd, itemListJsonLd, faqJsonLd]}
      />

      <main className="mdk-container specMain">
        {/* HERO */}
        <section className="specHero">
          <div className="specHeroGlow" aria-hidden="true" />

          <div className="specHeroInner">
            <div className="specHeroLeft">
              <div className="specKicker">
                <span className="specKickerIcon">
                  <Stethoscope size={16} />
                </span>
                <span className="specKickerText">{t.specialtyTag}</span>
              </div>

              <h1 className="specH1">{t.title}</h1>
              <p className="specSub">{t.subtitle}</p>

              <div className="specSearchRow">
                <div className="specSearchBox">
                  <Search size={18} />
                  <input
                    value={qText}
                    onChange={(e) => setQText(e.target.value)}
                    placeholder={t.searchPlaceholder}
                    className="specSearchInput"
                    inputMode="search"
                    autoComplete="off"
                    aria-label={t.searchPlaceholder}
                  />
                  {!!qText && (
                    <button className="specClearBtn" onClick={onClear} type="button" aria-label={t.clear}>
                      <X size={16} />
                    </button>
                  )}
                </div>

                <button
                  onClick={() => {
                    onScrollToList();
                    onBrowse();
                  }}
                  className="specPrimaryBtn"
                  type="button"
                >
                  <span>{t.ctaPrimary}</span>
                  <ArrowRight size={18} />
                </button>
              </div>

              <div className="specMetaRow">
                <div className="specCountPill">
                  <Star size={14} />
                  <span>{loading ? "…" : filtered.length} {t.results}</span>
                </div>
                <div className="specHint">{t.hint}</div>
              </div>

              <div className="specTrustGrid">
                {t.trust.map((x) => (
                  <div key={x.title} className="specTrustCard">
                    <div className="specTrustIcon">
                      <TrustIcon type={x.icon} />
                    </div>
                    <div className="specTrustText">
                      <div className="specTrustTitle">{x.title}</div>
                      <div className="specTrustSub">{x.sub}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="specHeroRight">
              <div className="specImgCard">
                <img
                  src={heroImage}
                  alt={lang === "en" ? "Dermatology" : "Dermatologji"}
                  className="specHeroImg"
                  loading="eager"
                  fetchPriority="high"
                />
                <div className="specImgShine" aria-hidden="true" />
                <div className="specSplitLine" aria-hidden="true" />
                <div className="specImgPill">{t.heroPill}</div>
              </div>
            </div>
          </div>
        </section>

        {/* LIST */}
        <section className="specSection" ref={listRef}>
          <div className="specSectionHead">
            <h2 className="specH2">{t.topTitle}</h2>
            <Link to={lang === "en" ? "/en" : "/sq"} className="specLink">
              {t.backHome}
            </Link>
          </div>

          <div className="mdk-list">
            {loading ? (
              <>
                <SkeletonRow />
                <SkeletonRow />
                <SkeletonRow />
              </>
            ) : filtered.length === 0 ? (
              <div className="specMutedBox">
                <div>{t.empty}</div>
                <div className="specEmptyHint">{t.emptyHint}</div>
              </div>
            ) : (
              filtered.slice(0, 60).map((d) => <DoctorCardRow key={d.id} d={d} t={t} lang={lang} />)
            )}
          </div>
        </section>

        {/* Popular reasons */}
        <section className="specCardSection">
          <h3 className="specH3">{t.popular.title}</h3>
          <div className="specChips">
            {t.popular.items.map((x) => (
              <button
                key={x}
                onClick={() => {
                  setQText(x);
                  onScrollToList();
                }}
                className="specChip"
                type="button"
              >
                {x}
              </button>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="specCardSection">
          <h3 className="specH3">{t.faq.title}</h3>
          <div className="specFaq">
            {t.faq.items.map((f) => (
              <details key={f.q} className="specFaqItem">
                <summary className="specFaqQ">{f.q}</summary>
                <div className="specFaqA">{f.a}</div>
              </details>
            ))}
          </div>
        </section>

        {/* Internal links */}
        <section className="specCardSection">
          <h3 className="specH3">{t.otherLinksTitle}</h3>
          <div className="specMiniLinks">
            <Link to={`/${lang === "en" ? "en" : "sq"}/Tirana/Kardiologji?lang=${lang}`} className="specMiniLink">
              {lang === "en" ? "Cardiology in Tirana" : "Kardiologji në Tiranë"}
            </Link>
            <Link to={`/${lang === "en" ? "en" : "sq"}/Tirana/Okulistike?lang=${lang}`} className="specMiniLink">
              {lang === "en" ? "Ophthalmology in Tirana" : "Okulistikë në Tiranë"}
            </Link>
            <Link to={`/${lang === "en" ? "en" : "sq"}/Tirana/Ortopedi?lang=${lang}`} className="specMiniLink">
              {lang === "en" ? "Orthopedics in Tirana" : "Ortopedi në Tiranë"}
            </Link>
          </div>
        </section>
      </main>

      <SiteFooter t={copy[lang]} />
    </div>
  );
}
